import { MAX_SIGNATURE_DATA_URL_LENGTH, MAX_SIGNATURE_IMAGE_BYTES } from '../constants/signing';

export type SignatureValidationResult =
  | { ok: true }
  | { ok: false; message: string };

// H-5: include webp and avif — canvas drawImage decodes them natively on modern browsers
const ALLOWED_SIGNATURE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/avif',
] as const;

// FX-C6 Fix 4: removed \s — standard data URLs have no whitespace in the base64 body;
// the server rejects whitespace so the client must do the same.
const SIGNATURE_DATA_URL_RE = /^data:image\/(png|jpeg|jpg|webp|avif);base64,[A-Za-z0-9+/=]+$/;

export function validateSignatureDataUrl(dataUrl: string): SignatureValidationResult {
  if (!SIGNATURE_DATA_URL_RE.test(dataUrl)) {
    return { ok: false, message: 'Signature image must be a PNG, JPEG, or WebP data URL.' };
  }

  if (dataUrl.length > MAX_SIGNATURE_DATA_URL_LENGTH) {
    return { ok: false, message: 'Signature image is too large. Use an image under 2 MB.' };
  }

  return { ok: true };
}

export function readSignatureImageFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    // H-5: allow webp and avif in addition to png/jpeg
    if (!(ALLOWED_SIGNATURE_MIME_TYPES as readonly string[]).includes(file.type)) {
      reject(new Error('Signature image must be a PNG, JPEG, or WebP file.'));
      return;
    }

    if (file.size > MAX_SIGNATURE_IMAGE_BYTES) {
      reject(new Error('Signature image is too large. Use an image under 2 MB.'));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read signature image file.'));
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      // The data URL from FileReader will have the correct MIME; validate it passes our regex
      const validation = validateSignatureDataUrl(dataUrl);
      if (!validation.ok) {
        reject(new Error(validation.message));
        return;
      }
      resolve(dataUrl);
    };
    reader.readAsDataURL(file);
  });
}
